import { useCallback } from 'react';
import { atom, useRecoilState, useResetRecoilState } from 'recoil';
import { Place } from 'features/places/places.types';
import { replaceAtIndex } from 'shared/utils';
import { localStorageEffect } from 'libs/recoil';
import { LOCAL_STORAGE_KEY } from './localStorage';

type MapPlacesState = {
  places: Place[];
  updatedAt: number;
};

type UseMapPlacesState = {
  setMapPlaces: (places: Place[], updatedAt?: number) => void;
  addMapPlace: (placeLocation: Place, updatedAt?: number) => void;
  deleteMapPlace: (placeId: number, updatedAt?: number) => void;
  updateMapPlace: (placeLocation: Place, updatedAt?: number) => void;
  clearMapPlaces: () => void;
  places: Place[];
  updatedAt: number;
};

const DEFAULT_MAP_PLACES_STATE: MapPlacesState = {
  places: [],
  updatedAt: 0,
};

export const mapPlacesStateAtom = atom({
  key: LOCAL_STORAGE_KEY.MAP_PLACES,
  default: DEFAULT_MAP_PLACES_STATE,
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.MAP_PLACES)],
});

export const useMapPlacesState = (): UseMapPlacesState => {
  const [mapPlacesState, setMapPlacesState] =
    useRecoilState(mapPlacesStateAtom);
  const clearMapPlaces = useResetRecoilState(mapPlacesStateAtom);

  const setMapPlaces = useCallback(
    (places: Place[], updatedAt?: number) => {
      setMapPlacesState({ places, updatedAt: updatedAt || Date.now() });
    },
    [setMapPlacesState],
  );

  const addMapPlace = useCallback(
    (placeLocation: Place, updatedAt?: number) => {
      setMapPlacesState((prev) => ({
        places: [placeLocation, ...prev.places],
        updatedAt: updatedAt || Date.now(),
      }));
    },
    [setMapPlacesState],
  );

  const deleteMapPlace = useCallback(
    (placeId: number, updatedAt?: number) => {
      setMapPlacesState((prev) => ({
        places: prev.places.filter((place) => place.id !== placeId),
        updatedAt: updatedAt || Date.now(),
      }));
    },
    [setMapPlacesState],
  );

  const updateMapPlace = useCallback(
    (placeLocation: Place, updatedAt?: number) => {
      const placeIdx = mapPlacesState.places.findIndex(
        (place) => place.id === placeLocation.id,
      );

      if (placeIdx < 0) return;

      const mapPlaces = replaceAtIndex<Place>(
        mapPlacesState.places,
        placeIdx,
        placeLocation,
      );

      setMapPlacesState({
        places: mapPlaces,
        updatedAt: updatedAt || Date.now(),
      });
    },
    [mapPlacesState.places, setMapPlacesState],
  );

  return {
    ...mapPlacesState,
    setMapPlaces,
    addMapPlace,
    deleteMapPlace,
    updateMapPlace,
    clearMapPlaces,
  };
};
